import { useEffect, useState, useCallback } from "react";
import { Lock, LogOut, RefreshCw, Download, MessageCircle, CheckCircle2, Circle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  hero_form: "Formulario superior",
  final_cta_form: "Formulario inferior",
};

export default function AdminLeads() {
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [filterSource, setFilterSource] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const patchLead = async (lead, updates) => {
    setLeads((ls) => ls.map((x) => (x.id === lead.id ? { ...x, ...updates } : x)));
    await fetch(`${API}/api/leads/${lead.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updates),
    }).catch(() => {});
  };

  const toggleContact = (lead) => patchLead(lead, { contacted: !lead.contacted });
  const saveNote = (lead, notes) => {
    if ((lead.notes || "") !== notes) patchLead(lead, { notes });
  };

  const clearFilters = () => {
    setFilterSource("all");
    setFilterFrom("");
    setFilterTo("");
  };

  const loadLeads = useCallback(async () => {
    const r = await fetch(`${API}/api/leads`, { credentials: "include" });
    if (r.ok) setLeads(await r.json());
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (r.ok) {
          setUser(await r.json());
          loadLeads();
        } else setUser(false);
      } catch {
        setUser(false);
      }
    })();
  }, [loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Error al iniciar sesión");
      } else {
        setUser(data);
        loadLeads();
      }
    } catch {
      setError("Error de conexión");
    }
    setBusy(false);
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
    setLeads([]);
  };

  const sources = [...new Set(leads.map((l) => l.source).filter(Boolean))];
  const filtered = leads.filter((l) => {
    if (filterSource !== "all" && l.source !== filterSource) return false;
    const d = l.timestamp ? l.timestamp.slice(0, 10) : "";
    if (filterFrom && d < filterFrom) return false;
    if (filterTo && d > filterTo) return false;
    return true;
  });

  if (user === null) {
    return <div className="min-h-screen bg-[#1c242f] flex items-center justify-center text-[#a8ac9b] mono text-xs tracking-widest">CARGANDO…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#1c242f] flex items-center justify-center px-5" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#222e3b]/80 backdrop-blur-xl p-8">
          <img src="/images/logo_horizontal.webp" alt="EHÉCATL" className="h-10 w-auto mx-auto mb-6" />
          <h1 className="font-display text-2xl text-[#f4f2ea] text-center mb-1">Panel de leads</h1>
          <p className="text-xs text-[#a8ac9b] text-center mb-7 flex items-center justify-center gap-2">
            <Lock size={12} className="text-[#bfa360]" /> Acceso privado
          </p>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Correo"
            className="mb-4 w-full rounded-lg border border-white/10 bg-[#1c242f] px-4 py-3.5 text-sm text-[#f4f2ea] placeholder:text-[#5f6459] focus:outline-none focus:border-[#bfa360]/60"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            className="mb-5 w-full rounded-lg border border-white/10 bg-[#1c242f] px-4 py-3.5 text-sm text-[#f4f2ea] placeholder:text-[#5f6459] focus:outline-none focus:border-[#bfa360]/60"
          />
          {error && <p data-testid="admin-login-error" className="mb-4 text-center text-xs text-red-400">{error}</p>}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-[#bfa360] py-3.5 text-sm font-bold text-[#1c242f] hover:bg-[#ddc68c] transition-colors disabled:opacity-50"
          >
            {busy ? "Entrando…" : "Entrar"}
          </button>
          <a href="/" className="block mt-5 text-center text-xs text-[#a8ac9b] hover:text-[#ddc68c] transition-colors">← Volver al sitio</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1c242f] text-[#f4f2ea]" data-testid="admin-panel">
      <header className="border-b border-white/10 bg-[#121a23]">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/images/logo_horizontal.webp" alt="EHÉCATL" className="h-9 w-auto" />
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#bfa360]">Panel de leads</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={loadLeads} className="p-2.5 rounded-full border border-white/10 text-[#a8ac9b] hover:text-[#ddc68c] hover:border-[#bfa360]/50 transition-colors" aria-label="Actualizar">
              <RefreshCw size={15} />
            </button>
            <a
              data-testid="admin-export"
              href={`${API}/api/leads/export`}
              className="inline-flex items-center gap-2 rounded-full bg-[#bfa360] px-4 py-2.5 text-xs font-bold text-[#1c242f] hover:bg-[#ddc68c] transition-colors"
            >
              <Download size={14} /> Exportar CSV
            </a>
            <button data-testid="admin-logout" onClick={logout} className="p-2.5 rounded-full border border-white/10 text-[#a8ac9b] hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Salir">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        <p className="mono text-[11px] uppercase tracking-[0.25em] text-[#a8ac9b] mb-6">
          <span className="text-[#ddc68c]">{filtered.length}</span> contactos{filtered.length !== leads.length ? ` de ${leads.length}` : ""} registrados
        </p>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            data-testid="admin-filter-source"
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="rounded-lg border border-white/10 bg-[#1c242f] px-4 py-2.5 text-xs text-[#f4f2ea] focus:outline-none focus:border-[#bfa360]/60"
          >
            <option value="all">Todos los orígenes</option>
            {sources.map((s) => (
              <option key={s} value={s}>{SOURCE_LABELS[s] || s}</option>
            ))}
          </select>
          <input
            data-testid="admin-filter-from"
            type="date"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            className="rounded-lg border border-white/10 bg-[#1c242f] px-4 py-2.5 text-xs text-[#f4f2ea] focus:outline-none focus:border-[#bfa360]/60 [color-scheme:dark]"
          />
          <span className="text-[#5f6459] text-xs">a</span>
          <input
            data-testid="admin-filter-to"
            type="date"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            className="rounded-lg border border-white/10 bg-[#1c242f] px-4 py-2.5 text-xs text-[#f4f2ea] focus:outline-none focus:border-[#bfa360]/60 [color-scheme:dark]"
          />
          {(filterSource !== "all" || filterFrom || filterTo) && (
            <button data-testid="admin-filter-clear" onClick={clearFilters} className="text-xs text-[#ddc68c] link-line">
              Limpiar filtros
            </button>
          )}
        </div>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="bg-[#222e3b] text-left mono text-[10px] uppercase tracking-[0.18em] text-[#a8ac9b]">
                <th className="px-4 py-3.5">Registro</th>
                <th className="px-4 py-3.5">Nombre</th>
                <th className="px-4 py-3.5">WhatsApp</th>
                <th className="px-4 py-3.5">Prototipo</th>
                <th className="px-4 py-3.5">Visita</th>
                <th className="px-4 py-3.5">Origen</th>
                <th className="px-4 py-3.5">Estado</th>
                <th className="px-4 py-3.5">Notas</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className={`border-t border-white/5 hover:bg-white/[0.03] transition-colors ${l.contacted ? "opacity-45" : ""}`}>
                  <td className="px-4 py-3.5 text-[#a8ac9b] whitespace-nowrap">
                    {l.timestamp ? new Date(l.timestamp).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-4 py-3.5 font-medium">{l.name || "—"}</td>
                  <td className="px-4 py-3.5">
                    {l.phone ? (
                      <a href={`https://wa.me/52${l.phone.replace(/\D/g, "").replace(/^52/, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#25D366] hover:underline">
                        <MessageCircle size={13} /> {l.phone}
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3.5 text-[#a8ac9b]">{l.prototype || "—"}</td>
                  <td className="px-4 py-3.5 text-[#a8ac9b]">{l.preferred_date || "—"}</td>
                  <td className="px-4 py-3.5">
                    <span className="mono text-[9px] uppercase tracking-[0.15em] rounded-full border border-[#bfa360]/40 text-[#ddc68c] px-2.5 py-1">
                      {SOURCE_LABELS[l.source] || l.source}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <button
                      data-testid={`admin-contacted-${l.id}`}
                      onClick={() => toggleContact(l)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 mono text-[9px] uppercase tracking-[0.15em] border transition-colors ${
                        l.contacted
                          ? "border-[#25D366]/50 text-[#25D366]"
                          : "border-white/15 text-[#a8ac9b] hover:border-[#bfa360]/60 hover:text-[#ddc68c]"
                      }`}
                    >
                      {l.contacted ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                      {l.contacted ? "Contactado" : "Pendiente"}
                    </button>
                  </td>
                  <td className="px-4 py-3.5 min-w-[180px]">
                    <input
                      data-testid={`admin-notes-${l.id}`}
                      type="text"
                      defaultValue={l.notes || ""}
                      key={l.id + (l.notes || "")}
                      onBlur={(e) => saveNote(l, e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                      placeholder="Ej. llamar martes…"
                      className="w-full rounded-lg border border-white/10 bg-[#1c242f] px-3 py-2 text-xs text-[#f4f2ea] placeholder:text-[#5f6459] focus:outline-none focus:border-[#bfa360]/60"
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#5f6459]">Aún no hay contactos registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
